import { Mutex } from "async-mutex";
import Constants from "./constants";
import { IBearerToken } from "./interfaces";
import { isNullOrWhiteSpace } from "./objects";
import WebResponse, { ResponseTypeEnum } from "./WebResponse";

export type ApiCallback = ((result: any) => void) | null;
export type LoadingCallback = (() => void) | null;
export type ErrorCallback = ((errorMessage: string, redirectToErrorPage: boolean) => void) | null;

const mutex = new Mutex();

export default class APIHelper {

    public static GetBaseURL(): string { return this.BASE_URL; }

    public static SetBaseURL(url: string): void {
        this.BASE_URL = url;

        if (this.BASE_URL[this.BASE_URL.length - 1] !== "/") {
            this.BASE_URL = this.BASE_URL + "/";
        }
    }

    private static BASE_URL: string = "http://localhost:5000";

    private static preRequestCallback: LoadingCallback;
    private static postRequestCallback: ApiCallback;
    private static handleErrorCallback: ErrorCallback;

    public static registerLayoutCallback(preRequestCallback: LoadingCallback, postRequestCallback: ApiCallback, handleErrorCallback: ErrorCallback): void {
        this.preRequestCallback = preRequestCallback;
        this.postRequestCallback = postRequestCallback;
        this.handleErrorCallback = handleErrorCallback;
    }

    public static async GetAsync<T>(url: string, params?: any, body?: any, redirectOnError: boolean = true, contentType: string = "application/json", performPreCallback: boolean = true): Promise<WebResponse<T>> {
        return await this.FetchAndBuildWebResponseAsync("GET", url, params, body, redirectOnError, contentType, performPreCallback);
    }

    public static async CopyAsync<T>(url: string, params?: any, body?: any, redirectOnError: boolean = true): Promise<WebResponse<T>> {
        return await this.FetchAndBuildWebResponseAsync("COPY", url, params, body, redirectOnError);
    }

    public static async DeleteAsync<T>(url: string, params?: any, body?: any, redirectOnError: boolean = true): Promise<WebResponse<T>> {
        return await this.FetchAndBuildWebResponseAsync("DELETE", url, params, body, redirectOnError);
    }

    public static async PostAsync<T>(url: string, params?: any, body?: any, redirectOnError: boolean = true): Promise<WebResponse<T>> {
        return await this.FetchAndBuildWebResponseAsync("POST", url, params, body, redirectOnError);
    }

    public static async PutAsync<T>(url: string, params?: any, body?: any, redirectOnError: boolean = true): Promise<WebResponse<T>> {
        return await this.FetchAndBuildWebResponseAsync("PUT", url, params, body, redirectOnError);
    }

    public static Token(): string {
        var localTokenString: string | null = localStorage.getItem(Constants.LocalStorageNames.LOCAL_STORAGE_TOKEN_KEY);
        var localToken: IBearerToken | null = null;
        if (localTokenString) {
            try {
                localToken = JSON.parse(localTokenString);
            } catch {
                return "";
            }
        }
        return localToken?.token ?? "";
    }

    public static RefreshToken(): string {
        var localTokenString: string | null = localStorage.getItem(Constants.LocalStorageNames.LOCAL_STORAGE_TOKEN_KEY);
        var localToken: IBearerToken | null = null;
        if (localTokenString) {
            try {
                localToken = JSON.parse(localTokenString);
            } catch {
                return "";
            }
        }
        return localToken?.refreshToken ?? "";
    }

    //#region Private

    public static PrependBaseURL(url: string): string {

        if (url[0] === "/")
            url = url.slice(1);

        return `${this.BASE_URL}${url}`;
    }

    private static async FetchAndBuildWebResponseAsync<T>(httpVerb: string, urlString: string, params: any, body: any, redirectOnError: boolean, contentType: string = "application/json", performPreCallback: boolean = true): Promise<WebResponse<T>> {
        if (this.preRequestCallback && performPreCallback) { this.preRequestCallback(); }
        let res: Response;
        try {
            await mutex.waitForUnlock();
            res = await this.FetchAsync(httpVerb, urlString, params, body, contentType);
            const wr: WebResponse<T> = await this.BuildWebResponseAsync<T>(res);
            if (this.postRequestCallback) { this.postRequestCallback(wr); }
            if (this.handleErrorCallback && !wr.IsOk()) {
                this.handleErrorCallback(this.FormatErrorMessage<T>(wr, params, httpVerb, urlString), redirectOnError);
            }
            return wr;
        }
        catch (e) {
            if (this.handleErrorCallback) {
                this.handleErrorCallback(this.FormatErrorMessage<T>(null, params, httpVerb, urlString), redirectOnError);
            }
            return new WebResponse<T>(ResponseTypeEnum.Unknown);
        }
    }

    private static FormatErrorMessage<T>(wr: WebResponse<T> | null, params: any, httpVerb: string, urlString: string) {
        const message = wr?.Message ? `Message: ${wr.Message}` : "";
        const paramString = params ? `Params: ${JSON.stringify(params)}` : "";
        const responseType =  wr ? ResponseTypeEnum[wr.GetResponseType()] : "none";
        const errorMessage = `Fetch failed to ${httpVerb} at ${urlString} with response [${responseType}] ${message} ${paramString}`;
        return errorMessage;
    }

    public static async RefreshBearerToken(): Promise<boolean> {
        const refreshToken = this.RefreshToken();
        if (!isNullOrWhiteSpace(refreshToken)) {
            // Static request for a refresh token
            let refreshUrl = new URL(this.PrependBaseURL(`api/auth/renewtoken`));
            const refreshResult = await fetch(refreshUrl, {
                headers: {
                    "Accept": "application/json",
                    "Content-Type": "application/json",
                },
                method: "POST",
                mode: "cors",
                credentials: "include",
                body: JSON.stringify({
                    refreshToken: refreshToken
                })
            });
            if (refreshResult.ok) {
                const token = JSON.parse(await refreshResult.text()) as IBearerToken;
                if (!isNullOrWhiteSpace(token.token) && !isNullOrWhiteSpace(token.refreshToken)) {
                    // Successful refresh; update the stored token
                    localStorage.setItem(Constants.LocalStorageNames.LOCAL_STORAGE_TOKEN_KEY, JSON.stringify(token));
                    return true;
                }
            }
        }
        return false;
    }

    private static async FetchAsync(httpVerb: string, urlString: string, params?: any, body?: any, contentType: string = "application/json"): Promise<Response> {

        let url: URL;
        url = new URL(this.PrependBaseURL(urlString));
        
        if (params) {
            url.search = new URLSearchParams(params).toString();
        }

        let headers: any = {
            "Accept": contentType,
            "Content-Type": contentType,
        }

        let token = this.Token();

        if (!isNullOrWhiteSpace(token)) {
            headers["Authorization"] = `Bearer ${this.Token()}`;
        }

        const fetchParams: RequestInit = {
            headers,
            method: httpVerb,
            mode: "cors",
            credentials: "include",
            body: JSON.stringify(body),
        };
        
        let response = await fetch(url.toString(), fetchParams);
        if (response.status === 419) {
            // If refresh token exists, use that to try and get a new token
            if (mutex.isLocked()) {
                // Something is already trying to refresh; wait till it's done, then try again with the new token
                await mutex.waitForUnlock();
            } else {
                const release = await mutex.acquire();
                try {
                    await this.RefreshBearerToken();
                } finally {
                    release();
                }
            }
            token = this.Token();
            if (!isNullOrWhiteSpace(token)) {
                headers["Authorization"] = `Bearer ${this.Token()}`;
                response = await fetch(url.toString(), fetchParams);
            }
        }

        return response;
    }

    private static async BuildWebResponseAsync<T>(res: Response): Promise<WebResponse<T>> {
        let wr: WebResponse<T>;

        if (res.status === 404) {
            //If the token expires, webenezer will start returning 404.
            wr = new WebResponse<T>(ResponseTypeEnum.UnAuthorized);
            wr.AuthRequired = true;
            const jsonText: string = await res.text();
            if (jsonText.length > 0) {
                wr.Message = JSON.parse(jsonText);
            }
        } else if (res.status === 403) {
            wr = new WebResponse<T>(ResponseTypeEnum.Forbidden);
            wr.AuthRequired = true;
            const jsonText: string = await res.text();
            if (jsonText.length > 0) {
                wr.Message = jsonText;
            }
        } else if (res.status === 401) {
            wr = new WebResponse<T>(ResponseTypeEnum.UnAuthorized);
            wr.AuthRequired = true;
        } else if (res.status === 400) {
            wr = new WebResponse<T>(ResponseTypeEnum.BadRequest);
            const jsonText: string = await res.text();
            if (jsonText.length > 0) {
                wr.Message = JSON.parse(jsonText);
            }
        } else if (res.status === 302) {
            wr = new WebResponse<T>(ResponseTypeEnum.Found);
            const location = res.headers.get("Location");
            if (location) {
                wr.Location = location;
            }
        } else if (res.status === 301) {
            wr = new WebResponse<T>(ResponseTypeEnum.Redirect);
        } else if (res.status === 200) {
            wr = new WebResponse<T>(ResponseTypeEnum.Ok);
            const contentType = res.headers.get("Content-Type")
            if (contentType && !contentType.includes("application/json")) {
                wr.Result = await res.blob() as unknown as T;
                wr.FileName = APIHelper.GetFileNameFromResponse(res);
            }
            else {
                const jsonText: string = await res.text();
                if (jsonText.length > 0) {
                    wr.Result = JSON.parse(jsonText);
                }
            }
            wr.AuthRequired = false;
        } else if (res.status === 204) {
            // success, but no content
            wr = new WebResponse<T>(ResponseTypeEnum.Ok);
        } else if (res.status === 500) {
            wr = new WebResponse<T>(ResponseTypeEnum.ServerError);
        } else if (res.status === 503) {
            wr = new WebResponse<T>(ResponseTypeEnum.ServiceUnavailable);
            wr.AuthRequired = true;
        } else if (res.status === 419) {
            wr = new WebResponse<T>(ResponseTypeEnum.UnAuthorized);
            wr.AuthRequired = true;
        } else {
            // FUTURE; handle other statuses in a cool way if we find a need
            wr = new WebResponse<T>(ResponseTypeEnum.Unknown);
            wr.Message = res.statusText;
        }
        return wr;
    }

    private static GetFileNameFromResponse(res: Response): string {
        const header = res.headers.get('content-disposition');
        let fileName = header?.split(';')
            ?.find(n => n.includes('filename='))
            ?.replace('filename=', '')?.trim() ?? "";

        // The content-disposition header puts quotes around fileNames if they contain a space. We need to remove these
        if (fileName.startsWith('"') && fileName.endsWith('"')) {
            fileName = fileName.slice(1, -1)
        }

        return fileName
    }

    //#endregion

}
